import React, { useEffect, useState } from "react";
import { useSOC } from "../context/SOCContext";
import { DataTable } from "../../common/DataTable";
import Drawer, { DrawerSection, Timeline } from "../ui/Drawer";
import { SeverityBadge, StatusBadge } from "../ui/Badges";
import ApiLoader from "../../ui/ApiLoader";
import { INCIDENT_STATUSES } from "../data/mockData";

const Incidents = ({ focusId, onConsumeFocus, onNavigate }) => {
  const {
    incidents, alerts, events, investigations, responses,
    updateIncidentStatus, openInvestigation, loading, errors,
  } = useSOC();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (focusId) {
      const inc = incidents.find((i) => i.id === focusId);
      if (inc) setSelected(inc);
      onConsumeFocus && onConsumeFocus();
    }
    // eslint-disable-next-line
  }, [focusId, incidents]);

  useEffect(() => {
    if (selected) {
      const fresh = incidents.find((i) => i.id === selected.id);
      if (fresh && fresh !== selected) setSelected(fresh);
    }
    // eslint-disable-next-line
  }, [incidents]);

  const uniqueCategories = [...new Set(incidents.map((i) => i.category).filter(Boolean))];
  const uniqueSources = [...new Set(incidents.map((i) => i.source).filter(Boolean))];
  const uniqueAnalysts = [...new Set(incidents.map((i) => i.analyst).filter(Boolean))];

  const columns = [
    {
      id: "id",
      accessorFn: (r) => r.id,
      header: "Incident ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Incident ID", placeholder: "Search incident ID" } },
      cell: ({ row }) => <span>{row.original.id}</span>,
    },
    {
      id: "name",
      accessorFn: (r) => r.name,
      header: "Incident Name",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Incident Name", placeholder: "Search incident name" } },
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      id: "severity",
      accessorFn: (r) => r.severity,
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: ["critical", "high", "medium", "low"].map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
    },
    {
      id: "category",
      accessorFn: (r) => r.category,
      header: "Category",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Category",
          placeholder: "All categories",
          options: uniqueCategories.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.category}</span>,
    },
    {
      id: "source",
      accessorFn: (r) => r.source,
      header: "Source",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Source",
          placeholder: "All sources",
          options: uniqueSources.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.source}</span>,
    },
    {
      id: "affectedAssetName",
      accessorFn: (r) => r.affectedAssetName,
      header: "Affected Asset",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Affected Asset", placeholder: "Search asset" } },
      cell: ({ row }) => <span>{row.original.affectedAssetName}</span>,
    },
    {
      id: "affectedUser",
      accessorFn: (r) => r.affectedUser,
      header: "Affected User",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Affected User", placeholder: "Search user" } },
      cell: ({ row }) => <span>{row.original.affectedUser}</span>,
    },
    {
      id: "createdTime",
      accessorFn: (r) => r.createdTime,
      header: "Created Time",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.createdTime).toLocaleString()}</span>,
    },
    {
      id: "analyst",
      accessorFn: (r) => r.analyst,
      header: "Assigned Analyst",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Analyst",
          placeholder: "All analysts",
          options: uniqueAnalysts.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.analyst}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: INCIDENT_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "priority",
      accessorFn: (r) => r.priority,
      header: "Priority",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Priority",
          placeholder: "All priorities",
          options: ["P1", "P2", "P3"].map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.priority}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={() => setSelected(row.original)}
          className="text-blue-600 hover:underline text-xs font-medium"
        >
          View
        </button>
      ),
    },
  ];

  const relAlerts = selected ? alerts.filter((a) => selected.relatedAlertIds.includes(a.id)) : [];
  const relEvents = selected ? events.filter((e) => selected.relatedEventIds.includes(e.id)) : [];
  const relInvestigation = selected ? investigations.find((i) => i.id === selected.investigationId) : null;
  const relResponses = selected ? responses.filter((r) => r.relatedIncidentId === selected.id) : [];

  if (loading.incidents && incidents.length === 0) {
    return <ApiLoader variant="spinner" label="Loading Incidents..." className="py-20" />;
  }

  return (
    <div>
      <DataTable
        columns={columns}
        data={incidents}
        getRowId={(r) => String(r.id)}
        emptyMessage={errors.incidents || "No records found"}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.name} subtitle={selected?.id}>
        {selected && (
          <>
            <DrawerSection label="Summary">
              <p>{selected.summary}</p>
            </DrawerSection>

            <DrawerSection label="Overview">
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                <span className="text-gray-400">Severity</span>
                <SeverityBadge severity={selected.severity} />
                <span className="text-gray-400">Status</span>
                <StatusBadge status={selected.status} />
                <span className="text-gray-400">Priority</span>
                <span>{selected.priority}</span>
                <span className="text-gray-400">Category</span>
                <span>{selected.category}</span>
                <span className="text-gray-400">Analyst</span>
                <span>{selected.analyst}</span>
              </div>
            </DrawerSection>

            <DrawerSection label="Related Alerts">
              {relAlerts.length === 0 && <p className="text-gray-400">None.</p>}
              {relAlerts.map((a) => (
                <button key={a.id} onClick={() => onNavigate("alerts", a.id)} className="block text-blue-600 hover:underline">
                  {a.name}
                </button>
              ))}
            </DrawerSection>

            <DrawerSection label="Related Events">
              {relEvents.length === 0 && <p className="text-gray-400">None.</p>}
              {relEvents.map((e) => (
                <p key={e.id} className="text-gray-500">
                  {e.type} - {new Date(e.timestamp).toLocaleString()}
                </p>
              ))}
            </DrawerSection>

            <DrawerSection label="Affected Assets & Users">
              <p className="text-gray-500">Asset: {selected.affectedAssetName}</p>
              <p className="text-gray-500">User: {selected.affectedUser}</p>
            </DrawerSection>

            <DrawerSection label="Investigation">
              {relInvestigation ? (
                <button onClick={() => onNavigate("investigations", relInvestigation.id)} className="text-blue-600 hover:underline">
                  {relInvestigation.title}
                </button>
              ) : (
                <button
                  onClick={() => openInvestigation(selected, relAlerts[0])}
                  className="px-3 py-1.5 text-xs rounded-lg bg-purple-600/20 text-purple-400 border border-purple-600/30 hover:bg-purple-600/30"
                >
                  Open Investigation
                </button>
              )}
            </DrawerSection>

            <DrawerSection label="Response Actions">
              {relResponses.length === 0 && <p className="text-gray-400">None yet.</p>}
              {relResponses.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-gray-500">
                  <span>{r.action} - {r.target}</span>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </DrawerSection>

            <DrawerSection label="Update Status">
              <div className="flex flex-wrap gap-2">
                {INCIDENT_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateIncidentStatus(selected.id, s)}
                    className={`px-3 py-1.5 text-xs rounded-lg border ${
                      selected.status === s
                        ? "bg-blue-100 border-blue-300 text-blue-700"
                        : "border-gray-200 text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </DrawerSection>

            <DrawerSection label="Timeline">
              <Timeline items={selected.timeline} />
            </DrawerSection>

            <DrawerSection label="Analyst Notes">
              <div className="space-y-2 mb-2">
                {selected.notes.length === 0 && <p className="text-gray-400">No notes yet.</p>}
                {selected.notes.map((n, i) => (
                  <p key={i} className="text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2">
                    {n}
                  </p>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
                />
                <button
                  onClick={() => {
                    if (!note.trim()) return;
                    selected.notes.push(note.trim());
                    setNote("");
                    setSelected({ ...selected });
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Add
                </button>
              </div>
            </DrawerSection>

            {selected.resolution && (
              <DrawerSection label="Resolution">
                <p>{selected.resolution}</p>
              </DrawerSection>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Incidents;
